import { Component, inject, computed, signal, ChangeDetectionStrategy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RippleModule } from 'primeng/ripple';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthService } from '../../services/auth/auth.service';
import { NavigationContextService } from '../../services/navigation-context/navigation-context.service';
import { CheckoutCtaService } from '../../services/checkout-cta/checkout-cta.service';
import { LoginComponent } from 'src/app/module/auth/pages/login/login.component';
import { EventRegisterStorageService } from 'src/app/module/events/pages/event-main/event-register/services/event-register-storage.service';

const TOP_LEVEL_ROUTES = ['/home', '/filter', '/perfil', '/ingressos', '/eventos/meus-eventos'];
const EVENT_DETAIL_PREFIX = '/eventos/detalhe/';

@Component({
  selector: 'app-mobile-bottom-nav',
  templateUrl: './mobile-bottom-nav.component.html',
  styleUrls: ['./mobile-bottom-nav.component.scss'],
  imports: [RouterModule, RippleModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileBottomNavComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly dialogService = inject(DialogService);
  private readonly navigationContext = inject(NavigationContextService);
  private readonly checkoutCtaService = inject(CheckoutCtaService);
  private readonly eventRegisterStorageService = inject(EventRegisterStorageService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly currentUrl = signal(this.router.url);

  protected readonly isLogged = computed(() => this.auth.isLogged());
  protected readonly isProducer = computed(() => this.isLogged() && this.auth.isProducer());

  protected readonly isSubPage = computed(() => {
    const path = this.currentUrl().split(/[?#]/)[0];
    return !TOP_LEVEL_ROUTES.some(route => path === route || path.startsWith(route + '/'));
  });

  protected readonly isEventDetail = computed(() =>
    this.currentUrl().split(/[?#]/)[0].startsWith(EVENT_DETAIL_PREFIX)
  );

  /**
   * Checkout ativo com cliente logado: o menu vira
   * Início | Ingressos | Continuar | Voltar — "Eventos" dá lugar a
   * "Ingressos" e o slot de "Ingressos" vira a ação principal da compra.
   */
  protected readonly checkoutCta = computed(() => this.checkoutCtaService.cta());
  protected readonly isCheckoutMode = computed(
    () => this.checkoutCta() !== null && this.isLogged()
  );

  protected onCheckoutContinue(): void {
    const cta = this.checkoutCta();
    if (!cta || cta.disabled() || cta.loading()) {
      return;
    }
    cta.run();
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => this.currentUrl.set(event.urlAfterRedirects));
  }

  protected goBack(): void {
    this.router.navigateByUrl(this.navigationContext.backTarget());
  }

  protected scrollToTickets(): void {
    if (!this.isBrowser) {
      return;
    }
    document.getElementById('event-tickets')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * "Eventos" não navega mais pra busca por nome (redundante com a
   * busca do header) — rola até a lista de eventos já exibida na home.
   * Se o usuário não estiver na home, navega pra lá primeiro.
   */
  protected goToEvents(): void {
    if (!this.isBrowser) {
      return;
    }

    const scrollToList = () =>
      document.getElementById('home-events-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (this.currentUrl().split(/[?#]/)[0] === '/home') {
      scrollToList();
      return;
    }

    this.router.navigateByUrl('/home').then(() => {
      requestAnimationFrame(() => setTimeout(scrollToList, 50));
    });
  }

  protected async createEvent(): Promise<void> {
    this.eventRegisterStorageService.deleteEventRegisterInfo();

    // Leaving the wizard first also recreates its active step when this action
    // is triggered while the organizer is already editing an event.
    if (this.currentUrl().split(/[?#]/)[0].startsWith('/eventos/registrar')) {
      await this.router.navigateByUrl('/eventos/meus-eventos', { skipLocationChange: true });
    }

    await this.router.navigateByUrl('/eventos/registrar/sobre');
  }

  protected onAccountClick(): void {
    if (this.isLogged()) {
      return;
    }
    this.dialogService.open(LoginComponent, {
      header: 'Login',
      width: 'min(92vw, 500px)',
      height: 'auto',
      closable: true,
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: true,
      modal: true,
    });
  }
}
